import React from 'react';
import { Flex, Skeleton, Typography } from 'antd';
import OrderShipmentValueCardItem from './OrderShipmentValueCardItem';
import { PaymentType, ShipmentFeeType } from '@/types';
import OrderShipmentDescription from './OrderShipmentDescription';
import { calculateCustomerWillPay, calculateTotalValue } from '@/utils';
import { useTranslation } from 'react-i18next';
import { useImportTableUid } from '@/hooks';
import { useLocation } from 'react-router-dom';
import { ROUTE } from '@/config';

const { Text } = Typography;

// Define the props interface
interface OrderShipmentValueCardProps {
  orderValue: number;
  shipmentFee: number;
  customShipmentFee: number;
  // walletFee: number;
  total: number;
  orderValueText: string;
  shipmentFeeText: string;
  customShipmentFeeText: string;
  // walletFeeText: string;
  totalText: string;
  currency: string;
  orderPriceLoading?: boolean;
  shipmentFeeType: ShipmentFeeType;
  paymentType: PaymentType;
  discountAmount: number;
  prepaymentAmount: number;
}

const OrderShipmentValueCard: React.FC<OrderShipmentValueCardProps> = ({
  orderValue,
  shipmentFee,
  customShipmentFee = 0,
  customShipmentFeeText,
  // walletFee,
  total,
  orderValueText,
  shipmentFeeText,
  // walletFeeText,
  totalText,
  currency,
  orderPriceLoading,
  shipmentFeeType,
  paymentType,
  discountAmount,
  prepaymentAmount,
}) => {
  const { t } = useTranslation();

  const location = useLocation();

  const tableUid = useImportTableUid((state) => state.importTableUid); // Access the Table UID setter from Zustand

  const isBulkImportEditing = location.pathname === ROUTE.IMPORT;

  return (
    <Flex
      vertical
      className='py-3 px-4 rounded-lg bg-sd-primary/10 border border-sd-primary/20'
    >
      <Flex vertical className='border-0 border-b border-sd-border border-solid mb-3 pb-1'>
        <OrderShipmentValueCardItem
          value={orderValue}
          label={orderValueText}
          isLoading={orderPriceLoading}
          currency={currency}
        />

        {(shipmentFee > 0 || customShipmentFee > 0) && (
          <OrderShipmentValueCardItem
            value={
              customShipmentFee > 0 && shipmentFeeType === ShipmentFeeType.BY_CUSTOMER
                ? customShipmentFee
                : shipmentFee
            }
            label={
              customShipmentFee > 0 && shipmentFeeType === ShipmentFeeType.BY_CUSTOMER
                ? customShipmentFeeText
                : shipmentFeeText
            }
            isLoading={orderPriceLoading}
            currency={currency}
          />
        )}

        {discountAmount > 0 && (
          <OrderShipmentValueCardItem
            value={-discountAmount}
            label={t('discount')}
            isLoading={orderPriceLoading}
            currency={currency}
          />
        )}

        {prepaymentAmount > 0 && (
          <OrderShipmentValueCardItem
            value={-prepaymentAmount}
            label={t('prepayment')}
            isLoading={orderPriceLoading}
            currency={currency}
          />
        )}
      </Flex>

      <Flex className='flex justify-between items-center'>
        <Text className='font-semibold text-sm text-sd-foreground'>
          {t('customer_will_pay')}
        </Text>
        <Flex className='gap-x-1'>
          {orderPriceLoading ? (
            <Skeleton.Input active size='small' style={{ width: 50 }} />
          ) : (
            <>
              <Text className='font-bold text-sm text-sd-foreground'>
                {isBulkImportEditing
                  ? calculateTotalValue(orderValue, shipmentFee)
                  : calculateCustomerWillPay(
                      paymentType,
                      shipmentFeeType,
                      orderValue,
                      shipmentFee,
                      customShipmentFee,
                      discountAmount,
                      prepaymentAmount,
                    )}
              </Text>
              <Text className='font-bold text-sm text-sd-foreground'>
                {` ${currency}`}
              </Text>
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default OrderShipmentValueCard;
